// lib/wealth-building/tools/calculators.js
// ═══════════════════════════════════════════════════════════
// REI CALCULATOR SUITE — config-driven registry for the Tools tab.
//
// Each calculator is { id, title, category, blurb, inputs[], compute }.
// `compute(values)` returns { outputs:[{label,value,tone}], note }.
//
// These are standalone quick-calcs. The Deal Dossier (Deals tab) uses
// lib/wealth-building/property/deal-math.js for full multi-strategy
// analysis; the Tools versions reimplement the standard formulas so each
// calculator is fast, self-contained, and independently testable.
// ═══════════════════════════════════════════════════════════

const money = (n) => (n < 0 ? '−$' : '$') + Math.abs(Math.round(n || 0)).toLocaleString();
const pct = (n) => (n || 0).toFixed(1) + '%';
const num = (v) => (typeof v === 'number' && !isNaN(v) ? v : 0);

// amortized monthly payment
function amort(P, annualRatePct, years) {
  const r = (num(annualRatePct) / 100) / 12;
  const n = num(years) * 12;
  if (!n) return 0;
  if (!r) return P / n;
  return P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

const REHAB_PSF = { cosmetic: 25, standard: 45, major: 80, full: 115 };

export const TOOL_CATEGORIES = ['Deal Analysis', 'Rental', 'Financing', 'Costs'];

export const CALCULATORS = [
  // ── Deal Analysis ──────────────────────────────────────────
  {
    id: 'arv', title: 'ARV Calculator', category: 'Deal Analysis',
    blurb: 'After-Repair Value from comparable $/sqft.',
    inputs: [
      { key: 'ppsf', label: 'Avg comp price / sqft', prefix: '$', def: 180 },
      { key: 'sqft', label: 'Subject square footage', def: 1500 },
    ],
    compute: (v) => {
      const arv = num(v.ppsf) * num(v.sqft);
      return { outputs: [{ label: 'Estimated ARV', value: money(arv), tone: 'pos' }],
        note: 'ARV = avg comp $/sqft × your square footage. Use 3–5 sold, renovated, nearby comps.' };
    },
  },
  {
    id: 'seventy', title: '70% Rule Calculator', category: 'Deal Analysis',
    blurb: 'Quick max-offer screen for a flip/BRRRR.',
    inputs: [
      { key: 'arv', label: 'ARV', prefix: '$', def: 300000 },
      { key: 'rehab', label: 'Rehab cost', prefix: '$', def: 50000 },
    ],
    compute: (v) => {
      const mao = num(v.arv) * 0.70 - num(v.rehab);
      return { outputs: [{ label: 'Max offer (70% rule)', value: money(mao), tone: mao > 0 ? 'pos' : 'neg' }],
        note: '(ARV × 0.70) − rehab. A fast screen, not a final number — tighten it with the MAO calculator.' };
    },
  },
  {
    id: 'mao', title: 'MAO Calculator', category: 'Deal Analysis',
    blurb: 'Max Allowable Offer with your margin + fees.',
    inputs: [
      { key: 'arv', label: 'ARV', prefix: '$', def: 300000 },
      { key: 'rehab', label: 'Rehab cost', prefix: '$', def: 50000 },
      { key: 'factor', label: 'ARV factor', suffix: '%', def: 70 },
      { key: 'costs', label: 'Closing + holding + profit', prefix: '$', def: 25000 },
    ],
    compute: (v) => {
      const mao = num(v.arv) * (num(v.factor) / 100) - num(v.rehab) - num(v.costs);
      return { outputs: [{ label: 'Max allowable offer', value: money(mao), tone: mao > 0 ? 'pos' : 'neg' }],
        note: 'MAO = (ARV × factor) − rehab − (closing + holding + your profit). Lower factor = more cushion.' };
    },
  },
  {
    id: 'rehab', title: 'Rehab Cost Estimator', category: 'Deal Analysis',
    blurb: 'Ballpark rehab by scope + square footage.',
    inputs: [
      { key: 'sqft', label: 'Square footage', def: 1500 },
      { key: 'scope', label: 'Scope', type: 'select', options: [
        { value: 'cosmetic', label: 'Cosmetic (paint/floor) ~$25/sqft' },
        { value: 'standard', label: 'Standard ~$45/sqft' },
        { value: 'major', label: 'Major ~$80/sqft' },
        { value: 'full', label: 'Full gut ~$115/sqft' },
      ], def: 'standard' },
      { key: 'contingency', label: 'Contingency', suffix: '%', def: 10 },
    ],
    compute: (v) => {
      const base = num(v.sqft) * (REHAB_PSF[v.scope] || 45);
      const total = base * (1 + num(v.contingency) / 100);
      return { outputs: [
        { label: 'Base estimate', value: money(base) },
        { label: 'With contingency', value: money(total), tone: 'pos' },
      ], note: 'A planning ballpark — always confirm with a contractor walkthrough + line-item bid.' };
    },
  },
  {
    id: 'wholesale', title: 'Wholesale Calculator', category: 'Deal Analysis',
    blurb: 'Your contract price + assignment spread.',
    inputs: [
      { key: 'arv', label: 'ARV', prefix: '$', def: 300000 },
      { key: 'rehab', label: 'Rehab cost', prefix: '$', def: 50000 },
      { key: 'fee', label: 'Your assignment fee', prefix: '$', def: 10000 },
    ],
    compute: (v) => {
      const buyerMao = num(v.arv) * 0.70 - num(v.rehab);
      const yourMax = buyerMao - num(v.fee);
      return { outputs: [
        { label: 'Your max contract price', value: money(yourMax), tone: yourMax > 0 ? 'pos' : 'neg' },
        { label: 'Your assignment spread', value: money(v.fee), tone: 'pos' },
      ], note: 'Contract at or below this, assign to a cash buyer at their 70%-rule MAO, keep the spread.' };
    },
  },
  // ── Rental ─────────────────────────────────────────────────
  {
    id: 'rental', title: 'Rental Property Calculator', category: 'Rental',
    blurb: 'Cash flow, cap rate, cash-on-cash, DSCR.',
    inputs: [
      { key: 'price', label: 'Purchase price', prefix: '$', def: 250000 },
      { key: 'down', label: 'Down payment', suffix: '%', def: 25 },
      { key: 'rate', label: 'Interest rate', suffix: '%', def: 7 },
      { key: 'term', label: 'Loan term (years)', def: 30 },
      { key: 'rent', label: 'Monthly rent', prefix: '$', def: 2100 },
      { key: 'vacancy', label: 'Vacancy', suffix: '%', def: 5 },
      { key: 'opex', label: 'Operating expenses', suffix: '% of rent', def: 35 },
    ],
    compute: (v) => {
      const loan = num(v.price) * (1 - num(v.down) / 100);
      const cashIn = num(v.price) * (num(v.down) / 100);
      const mort = amort(loan, v.rate, v.term);
      const effRent = num(v.rent) * (1 - num(v.vacancy) / 100);
      const opexM = num(v.rent) * (num(v.opex) / 100);
      const cashFlow = effRent - opexM - mort;
      const noi = (effRent - opexM) * 12;
      const cap = num(v.price) ? (noi / num(v.price)) * 100 : 0;
      const coc = cashIn ? (cashFlow * 12 / cashIn) * 100 : 0;
      const dscr = mort ? noi / (mort * 12) : 0;
      return { outputs: [
        { label: 'Monthly cash flow', value: money(cashFlow), tone: cashFlow >= 0 ? 'pos' : 'neg' },
        { label: 'Cap rate', value: pct(cap) },
        { label: 'Cash-on-cash', value: pct(coc), tone: coc >= 0 ? 'pos' : 'neg' },
        { label: 'DSCR', value: dscr.toFixed(2), tone: dscr >= 1.25 ? 'pos' : 'neg' },
        { label: 'Cash invested', value: money(cashIn) },
      ], note: 'DSCR ≥ 1.25 and positive cash flow are the usual go/no-go lines.' };
    },
  },
  {
    id: 'caprate', title: 'Cap Rate Calculator', category: 'Rental',
    blurb: 'Net operating income ÷ price.',
    inputs: [
      { key: 'rent', label: 'Monthly rent', prefix: '$', def: 2100 },
      { key: 'vacancy', label: 'Vacancy', suffix: '%', def: 5 },
      { key: 'expenses', label: 'Annual operating expenses', prefix: '$', def: 8800 },
      { key: 'price', label: 'Price / value', prefix: '$', def: 250000 },
    ],
    compute: (v) => {
      const noi = num(v.rent) * 12 * (1 - num(v.vacancy) / 100) - num(v.expenses);
      const cap = num(v.price) ? (noi / num(v.price)) * 100 : 0;
      return { outputs: [
        { label: 'Net operating income', value: money(noi) },
        { label: 'Cap rate', value: pct(cap), tone: 'pos' },
      ], note: 'Cap rate = NOI ÷ price. Compare against local cap rates for the asset class.' };
    },
  },
  {
    id: 'dscr', title: 'DSCR Calculator', category: 'Rental',
    blurb: 'Debt-service coverage ratio for a DSCR loan.',
    inputs: [
      { key: 'noi', label: 'Annual NOI', prefix: '$', def: 17000 },
      { key: 'payment', label: 'Monthly loan payment', prefix: '$', def: 1250 },
    ],
    compute: (v) => {
      const ds = num(v.payment) * 12;
      const dscr = ds ? num(v.noi) / ds : 0;
      return { outputs: [{ label: 'DSCR', value: dscr.toFixed(2), tone: dscr >= 1.25 ? 'pos' : (dscr >= 1 ? '' : 'neg') }],
        note: 'Most DSCR lenders want ≥ 1.20–1.25. Below 1.0 means the property doesn’t cover its own debt.' };
    },
  },
  // ── Financing ──────────────────────────────────────────────
  {
    id: 'hardmoney', title: 'Hard Money Calculator', category: 'Financing',
    blurb: 'Interest-only payment + total cost of the loan.',
    inputs: [
      { key: 'loan', label: 'Loan amount', prefix: '$', def: 200000 },
      { key: 'rate', label: 'Interest rate', suffix: '%', def: 11 },
      { key: 'points', label: 'Origination points', suffix: '%', def: 2 },
      { key: 'months', label: 'Term (months)', def: 9 },
      { key: 'fees', label: 'Other fees', prefix: '$', def: 1500 },
    ],
    compute: (v) => {
      const monthly = num(v.loan) * (num(v.rate) / 100) / 12;
      const pointsCost = num(v.loan) * (num(v.points) / 100);
      const total = monthly * num(v.months) + pointsCost + num(v.fees);
      return { outputs: [
        { label: 'Monthly (interest-only)', value: money(monthly), tone: 'neg' },
        { label: 'Points cost', value: money(pointsCost) },
        { label: 'Total cost of loan', value: money(total), tone: 'neg' },
      ], note: 'Hard money is short-term bridge capital — refinance into DSCR/conventional ASAP. (See lender comparison below.)' };
    },
  },
  {
    id: 'ltv', title: 'LTV Calculator', category: 'Financing',
    blurb: 'Loan-to-value ratio.',
    inputs: [
      { key: 'loan', label: 'Loan amount', prefix: '$', def: 180000 },
      { key: 'value', label: 'Property value', prefix: '$', def: 250000 },
    ],
    compute: (v) => {
      const ltv = num(v.value) ? (num(v.loan) / num(v.value)) * 100 : 0;
      return { outputs: [{ label: 'LTV', value: pct(ltv), tone: ltv <= 75 ? 'pos' : 'neg' }],
        note: 'Keep ≤ 75% per property for a buffer if values drop. Above 80% limits refinance options.' };
    },
  },
  {
    id: 'refinance', title: 'Refinance Calculator', category: 'Financing',
    blurb: 'Cash-out + new payment on a refi.',
    inputs: [
      { key: 'value', label: 'Appraised / ARV', prefix: '$', def: 300000 },
      { key: 'ltv', label: 'Refi LTV', suffix: '%', def: 75 },
      { key: 'payoff', label: 'Current loan / all-in', prefix: '$', def: 200000 },
      { key: 'rate', label: 'New rate', suffix: '%', def: 7.5 },
      { key: 'term', label: 'Term (years)', def: 30 },
    ],
    compute: (v) => {
      const newLoan = num(v.value) * (num(v.ltv) / 100);
      const cashOut = newLoan - num(v.payoff);
      const payment = amort(newLoan, v.rate, v.term);
      return { outputs: [
        { label: 'New loan', value: money(newLoan) },
        { label: 'Cash out (tax-free)', value: money(cashOut), tone: cashOut >= 0 ? 'pos' : 'neg' },
        { label: 'New monthly payment', value: money(payment) },
      ], note: 'In BRRRR, cash out ≥ your all-in = an infinite-return deal. Cash-out is debt, so it’s not taxed.' };
    },
  },
  // ── Costs ──────────────────────────────────────────────────
  {
    id: 'holding', title: 'Holding Cost Calculator', category: 'Costs',
    blurb: 'Total carry while you hold/rehab/sell.',
    inputs: [
      { key: 'taxes', label: 'Property taxes (monthly)', prefix: '$', def: 250 },
      { key: 'insurance', label: 'Insurance (monthly)', prefix: '$', def: 150 },
      { key: 'utilities', label: 'Utilities (monthly)', prefix: '$', def: 120 },
      { key: 'loan', label: 'Loan interest (monthly)', prefix: '$', def: 1100 },
      { key: 'other', label: 'Other (HOA/lawn) monthly', prefix: '$', def: 80 },
      { key: 'months', label: 'Months held', def: 6 },
    ],
    compute: (v) => {
      const perMonth = num(v.taxes) + num(v.insurance) + num(v.utilities) + num(v.loan) + num(v.other);
      const total = perMonth * num(v.months);
      return { outputs: [
        { label: 'Carry per month', value: money(perMonth), tone: 'neg' },
        { label: 'Total holding cost', value: money(total), tone: 'neg' },
      ], note: 'Time is money: every extra month on market is real cash. Underwrite a realistic timeline + a buffer.' };
    },
  },
  {
    id: 'roi', title: 'ROI Calculator', category: 'Costs',
    blurb: 'Return on cash invested + annualized.',
    inputs: [
      { key: 'profit', label: 'Net profit', prefix: '$', def: 35000 },
      { key: 'cash', label: 'Cash invested', prefix: '$', def: 60000 },
      { key: 'months', label: 'Hold (months)', def: 8 },
    ],
    compute: (v) => {
      const roi = num(v.cash) ? (num(v.profit) / num(v.cash)) * 100 : 0;
      const annualized = num(v.months) ? roi * (12 / num(v.months)) : roi;
      return { outputs: [
        { label: 'ROI', value: pct(roi), tone: roi >= 0 ? 'pos' : 'neg' },
        { label: 'Annualized ROI', value: pct(annualized), tone: annualized >= 0 ? 'pos' : 'neg' },
      ], note: 'Annualized ROI lets you compare a fast flip against a slow hold on equal footing.' };
    },
  },
];

export const CALCULATORS_BY_ID = Object.fromEntries(CALCULATORS.map((c) => [c.id, c]));

// Disclosed partner comparisons (the affiliate / options-panel monetization —
// see DEAL_INTELLIGENCE_DOMAIN.md partner map). Replace with live partner data.
export const LENDER_COMPARISONS = {
  'hard-money': {
    title: 'Compare Hard Money Lenders',
    blurb: 'Short-term bridge capital for acquisition + rehab.',
    partners: [
      { name: 'Kiavi', highlight: 'Pre-approved in 24h, close 10–14 days', note: 'Up to ~90% purchase / 100% rehab' },
      { name: 'New Silver', highlight: '5-minute approval', note: 'Fix-and-flip + rental bridge' },
    ],
  },
  'dscr': {
    title: 'Compare DSCR Lenders',
    blurb: 'Long-term rental loans qualified on the property’s income, not your DTI.',
    partners: [
      { name: 'Kiavi DSCR', highlight: 'No personal income docs', note: 'DSCR from ~1.0; unlimited properties' },
      { name: 'Visio / others', highlight: '30-yr fixed available', note: 'Vacation + LTR; portfolio options' },
    ],
  },
};

export const DISCLOSURE = 'We may earn a commission if you apply through these partners — at no extra cost to you.';
